// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductContextAvailability from "@bg/Overrides/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@bg/Overrides/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@bg/Overrides/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@bg/Overrides/Components/ProductPartNumbers";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Badge from "@insite/mobius/Badge";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: ProductCarouselProductStyles;
    customActionsTemplate?: (product: ProductModel) => React.ReactNode;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productItem?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    priceStlye?: ProductPriceStyles;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: InjectableCss;
    qtyGridItem?: InjectableCss;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: InjectableCss;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    customActionStyle?: InjectableCss;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productItem: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    productImageStyles: {
        wrapper: {
            css: css`
                position: relative;
                width: 100%;
                margin-bottom: 20px;
            `,
        },
        linkWrappingImage: {
            css: css`
                height: 236px;
                width: 236px;
                padding: 18px;
                display: flex;
                justify-content: center;
                align-items: center;
                @media (max-width: 1400px) {
                    &&& {
                        width: 100%;
                    }
                }
            `,
        },
        image: {
            cloudinaryWidth: "200",
            css: css`
                img {
                    max-width: 200px;
                    max-height: 200px;
                }
            `,
        },
    },
    productDescriptionStyles: {
        productDetailLink: {
            typographyProps: {
                size: 18,
                lineHeight: "20px",
                css: css`
                    opacity: 0.8;
                    height: 44px;
                `,
            },
            css: css`
                margin-bottom: ${fontConfig.spacing.heading};
            `,
        },
        productDetailDescription: {
            size: 18,
            lineHeight: "20px",
            css: css`
                opacity: 0.8;
                margin-bottom: ${fontConfig.spacing.heading};
                height: 44px;
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                border-bottom: 1px solid ${getColor("common.ui2")};
                margin-bottom: 11px;
                height: 21px; // just in case the mfr is not there
            `,
        },
    },
    productPriceWrapper: {
        css: css`
            flex-grow: 1;
            margin-bottom: 10px;
            display: flex;
            flex-direction: column;
        `,
    },
    priceStlye: {
        wrapper: {
            css: css`
                width: auto;
            `,
        },
        price: {
            priceText: {
                size: "18px",
                lineHeight: "22px",
            },
        },
    },
    productAvailabilityStyles: {
        container: {
            css: css`
                display: flex;
                align-items: flex-start;
            `,
        },
        availabilityByWarehouseLink: {
            css: css`
                line-height: 0;
                margin-right: 2px;
                > span {
                    line-height: 20px;
                }
            `,
        },
    } as any,
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 10px;
            `,
        },
    },
    addToCartContainer: {
        css: css`
            display: flex;
        `,
    },
    qtyGridItem: {
        css: css`
            padding-right: 10px;
            flex-grow: 1;
        `,
    },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
            `,
        },
    },
    addToCartButtonGridItem: {
        css: css`
            .icon-only {
                width: 85px !important;
            }
        `,
    },
    productAddToListLinkStyles: {
        link: {
            css: css`
                width: 100%;
                border-bottom: 1px solid ${getColor("common.ui2")};
                margin-bottom: 11px;
                padding-bottom: 3px;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: fontConfig.weight.bold,
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    customActionsTemplate,
}: Props) => {
    if (!product || !productInfo) {
        return null;
    }

    const [styles] = useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const [isTouch, setIsTouch] = React.useState(false);

    useEffect(() => {
        // detecting if device is in touch mode to add the dragable ability for product carousel
        isTouchDevice() && setIsTouch(true);
    }, []);

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    return (
        <StyledWrapper className={`product-card ${isTouch ? "" : "no-swiping-elements"}`} {...styles.productItem}>
            <ProductContext.Provider value={productContext}>
                {customActionsTemplate && (
                    <StyledWrapper {...styles.customActionStyle}>{customActionsTemplate(product)}</StyledWrapper>
                )}
                {showImage && !showOverlayBadge && (
                    <ProductImage
                        product={productContext}
                        onClick={() => trackProductRecommendation(true)}
                        extendedStyles={styles.productImageStyles}
                    />
                )}
                {showImage && showOverlayBadge && overlayBadge && (
                    <StyledWrapper
                        {...styles.badgeAreaWrapper}
                        ref={badgeAreaWrapperRef}
                        data-test-selector="productCarouselBadge"
                    >
                        <ProductImage extendedStyles={styles.productImageStyles} product={productContext} />
                        <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                            <Badge
                                type={overlayBadge.badgeType}
                                value={
                                    overlayBadge.badgeType === "Image"
                                        ? overlayBadge.largeImageBadgePath
                                        : overlayBadge.displayText
                                }
                                badgeTextColor={overlayBadge.textColorHexCode}
                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                                textBadgeCss={styles.badgeText}
                                imageBadgeProps={styles.badgeImageProps}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                {showAddToList && (
                    <ProductAddToListLink
                        data-test-selector="addToListLink"
                        extendedStyles={styles.productAddToListLinkStyles}
                    />
                )}

                <ProductDescription
                    lines={2}
                    lineHeight={20}
                    product={productContext}
                    onClick={() => trackProductRecommendation(true)}
                    extendedStyles={styles.productDescriptionStyles}
                />

                {showPartNumbers && (
                    <ProductPartNumbers
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.productPartNumbersStyles}
                    />
                )}
                {showTextBadges && textBadges && (
                    <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                )}
                {showImageBadges && imageBadges && (
                    <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges ?? 4} />
                )}
                {showPrice && (
                    <StyledWrapper {...styles.productPriceWrapper}>
                        <VisuallyHidden as="label" htmlFor={`${carouselId}-uom`} id={`${carouselId}-uom-label`}>
                            {translate("U/M")}
                        </VisuallyHidden>
                        <ProductPrice
                            showUnitOfMeasure={true}
                            showLabel={false}
                            product={productContext}
                            extendedStyles={styles.priceStlye}
                        />
                        <ProductContextAvailability
                            stockOnly={true}
                            extendedStyles={styles.productAvailabilityStyles}
                        />
                    </StyledWrapper>
                )}
                {showAddToCart && (
                    <StyledWrapper {...styles.addToCartContainer}>
                        <StyledWrapper {...styles.qtyGridItem}>
                            <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                                {translate("QTY")}
                            </VisuallyHidden>
                            <ProductQuantityOrdered
                                labelOverride=""
                                extendedStyles={styles.productQuantityOrderedStyles}
                                id={`${product.id}-qty`}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.addToCartButtonGridItem}>
                            <ProductAddToCartButton
                                onAddToCartClick={() => trackProductRecommendation(false)}
                                data-test-selector="addToCartBtn"
                                extendedStyles={styles.productAddToCartButtonStyles}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                {product.unitOfMeasures && (
                    <ProductUnitOfMeasureSelect
                        labelOverride=""
                        extendedStyles={styles.productUnitOfMeasureSelectStyles}
                        id={`${carouselId}-uom`}
                    />
                )}
                <ProductQuantityBreakPricing extendedStyles={styles.productQuantityBreakPricingStyles} />
            </ProductContext.Provider>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
